import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft } from "lucide-react";
import { fetchArticle } from "@/lib/api";
import { ArticleCard } from "@/components/ArticleCard";
import { AdSlot } from "@/components/AdSlot";
import { ShareButtons } from "@/components/ShareButtons";
import { Seo } from "@/components/Seo";

export default function Article() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setData(null);
    setNotFound(false);
    fetchArticle(slug).then(setData).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="max-w-3xl mx-auto px-5 py-32 text-center">
        <Helmet>
          <title>404 — NERO SUN</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <h1 className="font-anton text-5xl uppercase">404</h1>
        <p className="text-muted-foreground mt-4">This story has left the building.</p>
        <Link to="/" className="inline-block mt-6 font-mono text-xs uppercase tracking-widest text-signal">
          ← Back home
        </Link>
      </div>
    );
  }

  if (!data) {
    return <p className="font-mono text-sm text-muted-foreground text-center py-32">Loading…</p>;
  }

  const { article, related } = data;

  return (
    <article>
      <Seo
        title={article.title}
        description={article.dek}
        image={article.image}
        type="article"
        path={`/article/${article.slug}`}
        noindex={!!article.draft}
      />
      <div className="relative grain h-[60vh] min-h-[420px] border-b border-white/10 overflow-hidden">
        <img src={article.image} alt={article.title} className="w-full h-full object-cover img-treatment" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
        <div className="absolute inset-0 max-w-4xl mx-auto px-5 lg:px-10 flex flex-col justify-end pb-12">
          <span className="font-mono text-xs uppercase tracking-[0.3em] text-signal mb-4">
            {article.category} {article.ai_generated && "· NS Studio"}
            {article.draft && <span className="ml-3 bg-signal text-white px-2 py-0.5" data-testid="article-draft-badge">Draft preview</span>}
          </span>
          <h1 className="font-display font-semibold tracking-tighter leading-[0.95] text-4xl md:text-6xl" data-testid="article-title">
            {article.title}
          </h1>
        </div>
      </div>

      <div className="max-w-[1100px] mx-auto px-5 lg:px-10 py-14 grid lg:grid-cols-[1fr_300px] gap-12">
        <div>
          <Link to="/" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-muted-foreground hover:text-signal mb-10">
            <ArrowLeft className="w-4 h-4" /> Back
          </Link>

          <p className="font-display text-2xl md:text-3xl leading-snug text-foreground/90 mb-8">
            {article.dek}
          </p>
          <div className="flex items-center gap-4 font-mono text-[11px] uppercase tracking-widest text-muted-foreground border-y border-white/10 py-4 mb-10">
            <span>By {article.author}</span>
            <span className="text-signal">/</span>
            <span>{article.read_time} min read</span>
          </div>

          <ShareButtons title={article.title} className="mb-10" />

          <div className="prose-invert space-y-6">
            {(article.body || "").split("\n\n").map((p, i) => (
              <div key={i}>
                <p className="text-foreground/85 text-lg leading-relaxed" data-testid={`article-paragraph-${i}`}>
                  {p}
                </p>
                {i === 1 && (
                  <AdSlot variant="leaderboard" slot={process.env.REACT_APP_ADSENSE_SLOT_INARTICLE} className="my-8" />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Sidebar ad */}
        <aside className="hidden lg:block">
          <div className="sticky top-28">
            <AdSlot variant="rectangle" slot={process.env.REACT_APP_ADSENSE_SLOT_SIDEBAR} />
          </div>
        </aside>
      </div>

      {related && related.length > 0 && (
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-16 border-t border-white/10">
          <h2 className="font-anton text-3xl uppercase tracking-tight mb-8">More in {article.category}</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {related.map((a, i) => (
              <ArticleCard key={a.slug} article={a} index={i} />
            ))}
          </div>
        </div>
      )}
    </article>
  );
}
